import styled from 'styled-components';
import { Label } from '../../../components/uielements';
import { key, palette } from 'styled-theme';

const desktopBreakpoint = key('breakpoints.desktop', '1024px');
const tabletBreakpoint = key('breakpoints.tablet', '820px');
const mobileBreakpoint = key('breakpoints.mobile', '480px');

const gap = {
  tiny: key('sizes.gap.tiny', '4px'),
  small: key('sizes.gap.small', '8px'),
  normal: key('sizes.gap.normal', '16px'),
  medium: key('sizes.gap.medium', '24px'),
  big: key('sizes.gap.big', '32px'),
};

const radius = {
  tiny: key('sizes.borderRadius.tiny', '4px'),
  small: key('sizes.borderRadius.small', '8px'),
};

const font = {
  small: key('sizes.font.small', '12px'),
  normal: key('sizes.font.normal', '14px'),
  medium: key('sizes.font.medium', '16px'),
};

const accent = palette('color', 0, '#6366f1');

export const Main = styled.main`
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 100vh;
  min-height: 100dvh;
  width: 100vw;

  @media (max-width: ${desktopBreakpoint}) {
    grid-template-columns: 1.2fr 1fr;
  }

  /* Right-hand image isn't needed on tablet or mobile — single column, form only */
  @media (max-width: ${tabletBreakpoint}) {
    grid-template-columns: 1fr;
  }
`;

export const SectionLeft = styled.section`
  background-color: ${palette('background', 0, '#ECEAF8')};
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: ${gap.medium};
  padding: ${gap.big} 120px;
  box-sizing: border-box;
  width: 100%;

  @media (max-width: ${desktopBreakpoint}) {
    padding: ${gap.big} 64px;
  }

  @media (max-width: ${tabletBreakpoint}) {
    padding: ${gap.big} ${gap.normal};
  }

  @media (max-width: ${mobileBreakpoint}) {
    padding: ${gap.normal};
    gap: ${gap.normal};
  }
`;

export const Wrapper = styled.div`
  display: grid;
  gap: ${gap.normal};
`;

export const SectionRight = styled.section`
  background-image: url(${(props) => props.bg});
  transition: background-image 0.5s ease-in-out;
  background-size: cover;
  background-position: center;

  @media (max-width: ${tabletBreakpoint}) {
    display: none;
  }
`;

export const Heading = styled(Label).attrs({ size: 'header2', color: 'black', weight: 'bold' })`
  text-transform: uppercase;
`;

export const SubHeading = styled(Label).attrs({ size: 'normal', color: 'black' })``;

export const SubLabel = styled(Label).attrs({ size: 'small', color: 'black' })`
  font-size: ${font.medium};
`;

export const SearchContainer = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: ${gap.medium};
`;

export const SuggestionsList = styled.ul`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin: 0;
  padding: 0;
  list-style: none;
  background-color: white;
  border: 1px solid #ddd;
  border-top: none;
  border-radius: 0 0 ${radius.tiny} ${radius.tiny};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
`;

export const SuggestionItem = styled.li`
  padding: ${gap.small} ${gap.normal};
  cursor: pointer;
  transition: background-color 0.2s ease;
  font-size: ${font.normal};

  &:hover {
    background-color: #f5f5f5;
  }

  &:not(:last-child) {
    border-bottom: 1px solid #eee;
  }
`;

export const ErrorMessage = styled.p`
  color: #a05c4b;
  font-size: ${font.normal};
`;

export const PhoneInputContainer = styled.div`
  display: grid;
  grid-auto-flow: column;
  width: 100%;
  gap: ${gap.small};
  grid-template-columns: 1fr 4fr;

  @media (max-width: ${mobileBreakpoint}) {
    grid-auto-flow: row;
    grid-template-columns: 1fr;
  }
`;

export const CountryCodeSelect = styled.select`
  font-size: ${font.normal};
  padding: ${gap.small};
  border: 1px solid ${palette('border', 0, '#D7D7E2')};
  border-radius: ${radius.tiny};
  background-color: #fff;
  color: #606266;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  min-width: 110px;
  width: 100%;
  height: 100%;

  &:focus {
    border-color: ${accent};
  }
`;

export const SelectWrapper = styled.div`
  position: relative;
`;

export const IconWrapper = styled.div`
  position: absolute;
  top: 55%;
  right: 10px;
  transform: translateY(-55%);
  pointer-events: none;
`;

export const TabletIntro = styled.div`
  display: none;

  @media (max-width: ${tabletBreakpoint}) {
    display: block;
    margin-bottom: ${gap.small};
  }
`;

export const PageTitle = styled.h1`
  font-family: ${key('fonts.primary', 'JetBrains Mono Regular')};
  font-size: 2rem;
  font-weight: 700;
  color: #1f1f1f;
  margin: 0 0 ${gap.tiny} 0;
`;

export const PageSubtitle = styled.p`
  font-family: ${key('fonts.primary', 'JetBrains Mono Regular')};
  font-size: ${font.medium};
  color: #6b7280;
`;

export const FormCard = styled.div`
  @media (min-width: calc(${mobileBreakpoint} + 1px)) and (max-width: ${tabletBreakpoint}) {
    background-color: ${palette('background', 3, '#f8f5fe')};
    border-radius: ${radius.small};
    padding: ${gap.big} ${gap.medium};
  }
`;
